/**
 * Wide-figure motion.
 *
 * For inline diagram figures whose image is much wider than tall (e.g. a
 * roadmap timeline), this:
 *   1. tags the figure `.rh-diagram--wide` (CSS gives it a fixed-height,
 *      edge-cropped viewport and a left-to-right draw-in wipe), and
 *   2. pans the image horizontally in step with page scroll, so scrolling
 *      forward moves you forward along the timeline.
 *
 * Narrow figures (e.g. the architecture diagram) are left untouched.
 * Respects prefers-reduced-motion.
 */
((Drupal) => {
  const WIDE_RATIO = 2.2;

  Drupal.behaviors.rhinoPan = {
    attach(context) {
      const figs = once('rh-pan', '.rh-diagram', context);
      if (!figs.length) return;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      figs.forEach((fig) => {
        const media = fig.querySelector('img, svg');
        if (!media) return;

        const init = () => {
          const w = media.naturalWidth || media.clientWidth;
          const h = media.naturalHeight || media.clientHeight;
          if (!w || !h || w / h < WIDE_RATIO) return; // not wide — leave static

          // Use the wipe instead of the generic fade for wide figures.
          fig.classList.remove('rh-reveal');
          fig.classList.add('rh-diagram--wide');

          const frame = fig.querySelector('.rh-diagram__frame');

          // Draw-in on first entry.
          if (reduce) {
            fig.classList.add('is-drawn');
            return;
          }
          const io = new IntersectionObserver(
            (entries) => {
              entries.forEach((e) => {
                if (e.isIntersecting) {
                  fig.classList.add('is-drawn');
                  io.unobserve(fig);
                }
              });
            },
            { threshold: 0.2 },
          );
          io.observe(fig);

          // Scroll-linked horizontal pan.
          let ticking = false;
          const update = () => {
            ticking = false;
            const rect = frame.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;
            const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
            const overflow = media.clientWidth - frame.clientWidth;
            if (overflow > 0) {
              media.style.transform = `translate3d(${(-progress * overflow).toFixed(1)}px, 0, 0)`;
            }
          };
          const onScroll = () => {
            if (!ticking) {
              ticking = true;
              requestAnimationFrame(update);
            }
          };
          window.addEventListener('scroll', onScroll, { passive: true });
          window.addEventListener('resize', onScroll);
          update();
        };

        if (media.tagName.toLowerCase() === 'svg' || media.complete) {
          init();
        } else {
          media.addEventListener('load', init, { once: true });
        }
      });
    },
  };
})(Drupal);
